// ServicesPage.jsx
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useLocalContext } from "../../context/LocalContext";
import {
  resolveSiteEmail,
  resolveSiteName,
  resolveSitePhone,
} from "../../utils/siteConfig";
import "./ServicesPage.css";
import {
  faCheckCircle,
  faShieldAlt,
  faRocket,
  faBolt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FaArrowRight } from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const ServicesPage = () => {
  const { serviceId } = useParams();
  const { webinfo = {} } = useLocalContext();

  const phoneCall = String(webinfo?.phonecall || webinfo?.phone || "").trim();
  const phoneText = resolveSitePhone(webinfo) || phoneCall;
  const company = resolveSiteName(webinfo);
  const contactEmail = resolveSiteEmail(webinfo);

  const services = useMemo(
    () => ({
      development: {
        title: "Web & Application Engineering",
        kicker: "Build • Performance • Security",
        description:
          "Production-ready websites and web apps built for speed, SEO, and scale — with clean architecture.",
        details:
          "We design and engineer modern web systems that stay fast as they grow. Whether you're launching an MVP or rebuilding a core product, we deliver a maintainable foundation with performance, security, and analytics baked in.",
        image:
          "https://res.cloudinary.com/dw2xu0tji/image/upload/v1750515877/Digital%20Marketting_2025-06-21_18_27/web-design-website-development-services.jpg",
        outcomes: [
          "Landing + service pages that convert",
          "Scalable frontend & backend architecture",
          "SEO structure + Core Web Vitals improvements",
          "Secure auth + role-based access (if needed)",
          "Dashboards, integrations & automation",
        ],
        growth: [
          { label: "Performance readiness", value: 92 },
          { label: "Security practices", value: 88 },
          { label: "Scalability foundation", value: 90 },
        ],
        highlights: [
          { icon: faBolt, text: "Speed-first builds with measurable Core Web Vitals" },
          { icon: faShieldAlt, text: "Secure patterns + least-privilege access" },
          { icon: faRocket, text: "Architecture that scales without rework" },
        ],
        testimonials: [
          {
            name: "Kabir Sinha",
            feedback:
              "Engineering quality was solid. We scaled traffic without performance drops.",
          },
          {
            name: "Nidhi Kapoor",
            feedback:
              "After the rebuild, the site felt faster and the conversion rate improved.",
          },
        ],
        sidebar: {
          timeline: "2–4 weeks (typical sprint)",
          deliverables: [
            "Scope + roadmap",
            "Build + QA checklist",
            "Launch assistance",
            "Post-launch recommendations",
          ],
          bestFor: ["Startups", "Service businesses", "Product launches", "Scaling teams"],
        },
      },
    }),
    []
  );

  const service = services[serviceId];
  if (!service) return <div style={{ padding: 24 }}>Service not found</div>;

  return (
    <div className="sp">
      {/* HERO */}
      <motion.section
        className="sp__hero"
        initial="hidden"
        animate="show"
        variants={fadeUp}
      >
        <div className="sp__heroGrid">
          <div>
            <div className="sp__crumb">
              <span className="sp__crumbDot" aria-hidden="true" />
              {service.kicker}
            </div>

            <h1 className="sp__heroTitle">
              {service.title.split(" ").slice(0, 2).join(" ")}{" "}
              <span>{service.title.split(" ").slice(2).join(" ")}</span>
            </h1>

            <p className="sp__heroSub">{service.description}</p>

            <div className="sp__heroActions">
              <a className="sp__btn sp__btn--primary" href="/contact">
                Get a quote <FaArrowRight />
              </a>

              {phoneCall ? (
                <a className="sp__btn sp__btn--ghost" href={`tel:${phoneCall}`}>
                  Call {phoneText}
                </a>
              ) : (
                <a className="sp__btn sp__btn--ghost" href="/contact">
                  Book a call
                </a>
              )}
            </div>
          </div>

          <div className="sp__heroCard">
            <div className="sp__heroCardTop">
              <div>
                <p className="sp__heroCardK">What’s included</p>
                <h3 className="sp__heroCardT">A complete delivery sprint</h3>
              </div>
              <span className="sp__heroChip">{service.sidebar.timeline}</span>
            </div>

            <div className="sp__heroImg">
              <img src={service.image} alt={service.title} />
            </div>

            <ul className="sp__heroList">
              {service.outcomes.slice(0, 4).map((o, idx) => (
                <li key={idx}>
                  <span className="sp__tick">✓</span>
                  {o}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.section>

      {/* BODY */}
      <div className="sp__wrap">
        {/* MAIN */}
        <motion.main
          className="sp__main"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          variants={fadeUp}
        >
          <section className="sp__sec">
            <h2 className="sp__h2">Overview</h2>
            <p className="sp__p">{service.details}</p>
          </section>

          <section className="sp__sec">
            <h2 className="sp__h2">What we deliver</h2>
            <ul className="sp__features">
              {service.outcomes.map((f, i) => (
                <li key={i}>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="sp__sec">
            <h2 className="sp__h2">Measured impact</h2>
            <div className="sp__bars">
              {service.growth.map((g, i) => (
                <div className="sp__barRow" key={i}>
                  <label>
                    <span>{g.label}</span>
                    <span>{g.value}%</span>
                  </label>
                  <div className="sp__barBg">
                    <div className="sp__barFill" style={{ width: `${g.value}%` }} />
                  </div>
                </div>
              ))}
            </div>

            <ul className="sp__features" style={{ marginTop: "1.4rem" }}>
              {service.highlights.map((h, i) => (
                <li key={i}>
                  <FontAwesomeIcon icon={h.icon} />
                  <span>{h.text}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="sp__sec">
            <h2 className="sp__h2">Client notes</h2>
            <div className="sp__tGrid">
              {service.testimonials.map((t, i) => (
                <div className="sp__t" key={i}>
                  <p>“{t.feedback}”</p>
                  <b>— {t.name}</b>
                </div>
              ))}
            </div>
          </section>
        </motion.main>

        {/* SIDEBAR */}
        <aside className="sp__side">
          <div className="sp__box">
            <h4>Best for</h4>
            <p>Teams that want a fast, measurable improvement — not endless redesign loops.</p>
            <ul className="sp__miniList">
              {service.sidebar.bestFor.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </div>

          <div className="sp__box">
            <h4>Delivery checklist</h4>
            <p>Clear steps, weekly updates, and a launch plan you can track.</p>
            <ul className="sp__miniList">
              {service.sidebar.deliverables.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </div>

          <div className="sp__box">
            <h4>Talk to {company}</h4>
            <p>
              Share your goals and we’ll recommend the highest-impact next steps.
            </p>
            <div style={{ marginTop: "1.2rem", display: "grid", gap: "0.9rem" }}>
              <a className="sp__btn sp__btn--primary" href="/contact">
                Start a project <FaArrowRight />
              </a>
              {phoneCall ? (
                <a className="sp__btn sp__btn--ghost" href={`tel:${phoneCall}`}>
                  Call {phoneText}
                </a>
              ) : (
                <a className="sp__btn sp__btn--ghost" href={`mailto:${contactEmail}`}>
                  Email us
                </a>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ServicesPage;